/**
 * Typed REST client for the owner dashboard.
 *
 * Mirrors every endpoint in `specs/001-investment-advisor/contracts/rest-api.md`
 * so the SPA talks to FastAPI with compile-time-checked shapes. The module is
 * pure (no React imports) and keeps I/O behind a single injectable `fetchImpl`
 * so tests can supply a `vi.fn()`.
 *
 * Conventions:
 *   - All requests send `credentials: "include"` so the HttpOnly `sid` cookie
 *     travels with the call (same-origin in dev via the Vite proxy).
 *   - State-changing calls echo the `csrf_token` double-submit cookie in an
 *     `X-CSRF-Token` header — matches `backend/api/middleware.py` (T066).
 *   - 401 triggers a hard redirect to `/auth/google` (the OAuth entry point)
 *     and throws a sentinel `ApiError` so the caller can bail out cleanly.
 *   - Non-2xx responses are mapped to `ApiError` with the server's machine
 *     `code`, ready for the Polish copy dictionary to translate.
 *   - 429 surfaces `retry_after_s` on the thrown error so the UI can render a
 *     cooldown countdown.
 */

import { keysToCamel } from "./snakeToCamel";

// ─── Shared primitive types ──────────────────────────────────────────────────

/** UTC ISO-8601 with millisecond precision, e.g. "2026-04-21T14:30:00.000Z". */
export type Iso8601 = string;

/** ISO-4217 uppercase currency code, e.g. "USD". */
export type Currency = string;

/** Monetary amount serialized as a JSON string to preserve precision. */
export type Money = string;

// ─── Domain entities (mirror contracts/rest-api.md §Common types) ────────────

export type PortfolioKind = "human" | "ai" | "tactical";

export interface PortfolioSummary {
  id: number;
  kind: PortfolioKind;
  label: string;
  base_currency: Currency;
}

export interface Holding {
  instrument_id: number;
  symbol: string;
  exchange: string;
  /** Decimal string; signed deltas are not emitted here. */
  quantity: string;
  /** Per-share average cost in the instrument's own currency. */
  avg_cost: Money;
  /** Converted to the portfolio's base currency using the snapshot FX. */
  market_value_base: Money;
}

export type CashBalance = Record<Currency, Money>;

export interface PortfolioSnapshot {
  taken_at: Iso8601;
  total_value_base: Money;
  cash_by_currency: CashBalance;
  holdings: Holding[];
}

/** Shape returned by `GET /api/portfolios/{id}/current`. */
export type PortfolioCurrent = PortfolioSnapshot;

/** Single chart point for `GET /api/portfolios/{id}/snapshots`. */
export interface PortfolioSnapshotPoint {
  taken_at: Iso8601;
  total_value_base: Money;
}

/** Full response body for `GET /api/portfolios/{id}/snapshots`. */
export interface PortfolioSnapshotSeries {
  portfolio_id: number;
  base_currency: Currency;
  points: PortfolioSnapshotPoint[];
}

export type ProposalStatus = "pending" | "accepted" | "rejected" | "expired" | "not_executable";

export type ProposalSide = "buy" | "sell" | "hold";

export type ProposalDecisionChoice = "accept" | "reject";

export type AiDecision = "buy" | "sell" | "hold";

export type DecisionSource = "telegram" | "web";

export interface SignalRef {
  id: number;
  kind: "news" | "price" | "macro" | "calendar";
  source: string;
  url: string | null;
  headline_pl: string | null;
  observed_at: Iso8601;
}

export interface ProposalSummary {
  /** UUIDv7. */
  id: string;
  created_at: Iso8601;
  expires_at: Iso8601;
  status: ProposalStatus;
  side: ProposalSide;
  symbol: string;
  exchange: string;
  quantity: string;
  limit_price: Money | null;
  currency: Currency;
}

export interface HumanDecisionEnvelope {
  choice: ProposalDecisionChoice;
  decided_at: Iso8601;
  latency_ms: number;
  source: DecisionSource;
}

export interface Proposal extends ProposalSummary {
  rationale_pl: string;
  signals: SignalRef[];
  ai_decision: AiDecision;
  /**
   * T084 will flip this to `true` on holidays / after-hours: the market is
   * closed but we surfaced the proposal for transparency. Declared now so the
   * UI doesn't need a second type refactor later.
   */
  market_only?: boolean;
  human_decision?: HumanDecisionEnvelope;
}

/** Response shape for `POST /api/proposals/{id}/decision`. */
export interface DecisionResponse {
  proposal_id: string;
  status: ProposalStatus;
  decided_at: Iso8601;
  trade?: { ibkr_order_id: string; filled: boolean };
}

/** Owner envelope returned by `GET /api/me`. */
export interface Owner {
  email: string;
  display_name: string;
  session_expires_at: Iso8601;
}

/** Rate-limit detail payload attached to 429 responses. */
export interface RateLimitDetail {
  retry_after_s: number;
}

// ─── Error envelope & client error type ──────────────────────────────────────

/**
 * Standard error envelope returned by every non-2xx response, per
 * `contracts/rest-api.md` §Error envelope.
 */
export interface ErrorEnvelope {
  error: {
    code: string;
    message: string;
    detail?: unknown;
  };
}

export interface ApiErrorInit {
  code: string;
  message: string;
  status: number;
  detail?: unknown;
  retryAfterS?: number | undefined;
}

/**
 * Typed error thrown by every fetcher in this module.
 *
 * `code` mirrors the stable machine code from the error envelope (or a
 * client-side sentinel like `network_error` / `redirecting`). Upstream code
 * maps the `code` to a Polish user-facing string via `lib/copy.ts`.
 */
export class ApiError extends Error {
  public readonly code: string;
  public readonly status: number;
  public readonly detail: unknown;
  public readonly retryAfterS: number | undefined;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = "ApiError";
    this.code = init.code;
    this.status = init.status;
    this.detail = init.detail;
    this.retryAfterS = init.retryAfterS;
  }
}

// ─── Cookie helper (exposed for consumers that want the csrf value) ─────────

/**
 * Tiny `document.cookie` parser. Returns `null` when the cookie is missing or
 * when running in an environment without `document` (e.g. SSR smoke tests).
 *
 * Note: we deliberately avoid a regex-global approach so cookies whose values
 * contain `=` survive intact.
 */
export function readCookie(name: string): string | null {
  if (typeof document === "undefined" || !document.cookie) {
    return null;
  }
  const needle = `${encodeURIComponent(name)}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(needle)) {
      return decodeURIComponent(part.slice(needle.length));
    }
  }
  return null;
}

// ─── Internals ───────────────────────────────────────────────────────────────

/**
 * Minimal fetch shape the client depends on. Narrower than the DOM lib's
 * `typeof fetch` so tests can stub with `vi.fn<Parameters<Fetch>, ...>` without
 * reproducing the whole overload surface.
 */
export type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientConfig {
  /** Injectable fetch — defaults to the global `fetch`. */
  fetchImpl?: Fetch;
  /**
   * Destination for 401 redirects. Defaults to `/auth/google` per the contract.
   * Overridable purely for testability.
   */
  loginUrl?: string;
  /**
   * Cookie name holding the CSRF double-submit value. Matches
   * `backend/api/auth.py::CSRF_COOKIE`.
   */
  csrfCookieName?: string;
}

const DEFAULT_LOGIN_URL = "/auth/google";
const DEFAULT_CSRF_COOKIE = "csrf_token";

function isErrorEnvelope(value: unknown): value is ErrorEnvelope {
  if (typeof value !== "object" || value === null) return false;
  const env = (value as { error?: unknown }).error;
  if (typeof env !== "object" || env === null) return false;
  const code = (env as { code?: unknown }).code;
  const message = (env as { message?: unknown }).message;
  return typeof code === "string" && typeof message === "string";
}

function pickRateLimitRetry(detail: unknown, headerFallback: string | null): number | undefined {
  if (detail && typeof detail === "object") {
    const raw = (detail as { retry_after_s?: unknown }).retry_after_s;
    if (typeof raw === "number" && Number.isFinite(raw)) return raw;
    if (typeof raw === "string") {
      const n = Number.parseInt(raw, 10);
      if (Number.isFinite(n)) return n;
    }
  }
  if (headerFallback) {
    const n = Number.parseInt(headerFallback, 10);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function buildQueryString(
  params: Record<string, string | number | boolean | string[] | undefined>,
): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue;
    if (Array.isArray(value)) {
      for (const v of value) qs.append(key, v);
    } else {
      qs.append(key, String(value));
    }
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

/**
 * Core request primitive. Every fetcher funnels through here so redirect /
 * envelope / rate-limit handling lives in exactly one place.
 */
async function request<T>(
  path: string,
  init: RequestInit,
  config: Required<Pick<ApiClientConfig, "fetchImpl" | "loginUrl" | "csrfCookieName">>,
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  const mutating = method !== "GET" && method !== "HEAD" && method !== "OPTIONS";

  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  headers.set("Accept", "application/json");
  if (mutating) {
    const csrf = readCookie(config.csrfCookieName);
    if (csrf) headers.set("X-CSRF-Token", csrf);
  }

  let response: Response;
  try {
    response = await config.fetchImpl(path, {
      ...init,
      method,
      headers,
      credentials: "include",
    });
  } catch (cause) {
    throw new ApiError({
      code: "network_error",
      message: cause instanceof Error ? cause.message : "network error",
      status: 0,
    });
  }

  if (response.status === 401) {
    // /api/me is the session-probe endpoint — 401 is expected when no
    // owner is signed in. Bouncing to /auth/google here would create an
    // infinite loop on /login?error=not_owner (SessionProvider runs on
    // every route, including the LoginPage itself). Just throw the error
    // so SessionProvider can set owner=null and let the route gate show
    // the LoginPage.
    const isSessionProbe = path === "/api/me" || path.endsWith("/api/me");
    if (!isSessionProbe) {
      if (typeof window !== "undefined" && window.location) {
        window.location.assign(config.loginUrl);
      }
      throw new ApiError({
        code: "redirecting",
        message: "redirecting to login",
        status: 401,
      });
    }
    throw new ApiError({
      code: "not_authenticated",
      message: "not authenticated",
      status: 401,
    });
  }

  if (response.status === 204) {
    return undefined as T;
  }

  // Try to parse JSON — tolerate empty bodies on error codes that legitimately
  // omit them (some proxies do this on 502/504).
  let parsed: unknown = undefined;
  const raw = await response.text();
  if (raw.length > 0) {
    try {
      parsed = JSON.parse(raw);
    } catch {
      // fall through; handled below
    }
  }

  if (!response.ok) {
    if (isErrorEnvelope(parsed)) {
      if (response.status === 429) {
        throw new ApiError({
          code: parsed.error.code,
          message: parsed.error.message,
          status: response.status,
          detail: parsed.error.detail,
          retryAfterS: pickRateLimitRetry(parsed.error.detail, response.headers.get("Retry-After")),
        });
      }
      throw new ApiError({
        code: parsed.error.code,
        message: parsed.error.message,
        status: response.status,
        detail: parsed.error.detail,
      });
    }
    // Non-envelope error — surface the raw status so the UI can still render
    // a sensible Polish fallback via the copy dictionary.
    throw new ApiError({
      code: response.status === 429 ? "rate_limited" : "http_error",
      message: `HTTP ${response.status}`,
      status: response.status,
      retryAfterS:
        response.status === 429
          ? pickRateLimitRetry(undefined, response.headers.get("Retry-After"))
          : undefined,
    });
  }

  return parsed as T;
}

// ─── Public factory + typed fetchers ─────────────────────────────────────────

export interface ListProposalsParams {
  status?: ProposalStatus[];
  from?: Iso8601;
  to?: Iso8601;
  limit?: number;
  cursor?: string;
}

export interface ListProposalsResponse {
  items: ProposalSummary[];
  next_cursor: string | null;
}

export type SnapshotGranularity = "daily" | "hourly";

export interface GetPortfolioSnapshotsParams {
  from?: Iso8601;
  to?: Iso8601;
  granularity?: SnapshotGranularity;
}

// ─── Tactical AI types (mirror docs/superpowers/specs/.../design.md §8) ─────

export type TacticalControlState = "active" | "paused" | "auto_paused";

export type TacticalCycle = "asia_open" | "eu_open" | "us_open";

export type TacticalRuleType =
  | "stop_loss"
  | "take_profit"
  | "rebalance"
  | "max_position"
  | "trailing_stop"
  | "brief_entry"
  | "brief_exit";

export type TacticalSignalType = "pct_move" | "gap" | "volume_spike" | "breakout";

export interface TacticalStatus {
  enabled: boolean;
  controlState: TacticalControlState;
  lastTickAt: Iso8601 | null;
  lastBriefAt: Iso8601 | null;
  lastTickAgeSeconds: number | null;
  consecutiveTickErrors: number;
  consecutiveInvalidBriefs: number;
  /** Decimal-as-string (or `null` while paused). */
  currentCash: Money | null;
  /** Decimal-as-string with sign — backend may render either as a number or a
   *  string. UI displays via Number() with sign formatting. */
  todayPnl: Money | number | null;
  tradesToday: number;
  pauseReason: string | null;
}

export interface TacticalPosition {
  symbol: string;
  mic: string;
  /** Decimal-as-string. */
  quantity: string;
  /** Decimal-as-string. */
  avgEntryPrice: Money;
  currency: Currency;
}

export interface TacticalPortfolio {
  baseCurrency: Currency;
  currentCash: Money;
  totalValue: Money;
  positions: TacticalPosition[];
}

export interface TacticalSnapshotPoint {
  takenAt: Iso8601;
  totalValue: Money;
}

export interface TacticalTrade {
  id: string;
  occurredAt: Iso8601;
  symbol: string;
  mic: string;
  side: "buy" | "sell";
  /** Decimal-as-string. */
  quantity: string;
  price: Money;
  currency: Currency;
  ruleType: TacticalRuleType;
  cashAfter: Money;
  briefId: string | null;
}

export interface ListTacticalTradesParams {
  from?: Iso8601;
  to?: Iso8601;
  limit?: number;
  /**
   * Offset-based paging — backend accepts `offset` as a query param. The
   * legacy `cursor` field is preserved for forward-compat: when supplied
   * and `offset` is omitted, it is sent as-is so a future cursor-based
   * implementation works without a client release.
   */
  offset?: number;
  cursor?: string;
}

export interface ListTacticalTradesResponse {
  items: TacticalTrade[];
  /** Total row count across the filtered window (offset-based paging). */
  total?: number;
  limit?: number;
  offset?: number;
  /** Reserved for future cursor-based paging — currently always null. */
  nextCursor?: string | null;
}

export interface TacticalSignal {
  id: string;
  detectedAt: Iso8601;
  symbol: string;
  mic: string;
  signalType: TacticalSignalType;
  /** Decimal-as-string of the move (e.g. "0.052" = +5.2%). Sign carried in the value. */
  magnitude: string;
  windowMinutes: number;
  priceNow: Money;
  pricePrev: Money;
  wasEscalated: boolean;
  escalatedProposalId: string | null;
  cooldownUntil: Iso8601 | null;
}

export interface ListTacticalSignalsParams {
  from?: Iso8601;
  to?: Iso8601;
}

export interface ListTacticalSignalsResponse {
  items: TacticalSignal[];
}

export interface TacticalBriefPerSymbolEntry {
  targetWeightPct: number;
  stopPct: number;
  takeProfitPct: number;
  maxPositionPct: number;
  bias: "long" | "short" | "neutral" | string;
  thesis: string;
}

export interface TacticalBriefGlobalRisk {
  maxTrades: number;
  maxAllocPct: number;
  maxDailyLossPct: number;
  anomalyThresholdPct: number;
}

export interface TacticalBrief {
  id: string;
  cycle: TacticalCycle;
  generatedAt: Iso8601;
  claudeModel: string;
  promptTokens: number;
  completionTokens: number;
  watchlist: string[];
  perSymbol: Record<string, TacticalBriefPerSymbolEntry>;
  globalRisk: TacticalBriefGlobalRisk;
  validationWarnings: string[] | null;
  isActive: boolean;
}

export interface TacticalPerformanceRule {
  ruleType: TacticalRuleType;
  samples: number;
  /** Decimal-as-string, signed (e.g. "-0.012" = −1.2%). */
  avgOutcome1d: string | null;
  avgOutcome5d: string | null;
}

export interface TacticalPerformance {
  windowDays: number;
  rules: TacticalPerformanceRule[];
}

export interface ApiClient {
  getMe(): Promise<Owner>;
  logout(): Promise<void>;
  listProposals(params?: ListProposalsParams): Promise<ListProposalsResponse>;
  getProposal(id: string): Promise<Proposal>;
  decideProposal(id: string, choice: ProposalDecisionChoice): Promise<DecisionResponse>;
  listPortfolios(): Promise<PortfolioSummary[]>;
  getPortfolioSnapshots(
    id: number,
    params?: GetPortfolioSnapshotsParams,
  ): Promise<PortfolioSnapshotPoint[]>;
  getPortfolioCurrent(id: number): Promise<PortfolioCurrent>;

  // Tactical AI surface — every fetcher resolves to `null` (or `void`) on
  // 503 so the dashboard can simply hide the Tactical sections when
  // TACTICAL_ENABLED=false.
  getTacticalStatus(): Promise<TacticalStatus | null>;
  pauseTactical(reason: string): Promise<void>;
  resumeTactical(confirm: boolean): Promise<void>;
  getTacticalPortfolio(): Promise<TacticalPortfolio | null>;
  getTacticalSnapshots(from: Iso8601, to: Iso8601): Promise<TacticalSnapshotPoint[] | null>;
  getTacticalTrades(params?: ListTacticalTradesParams): Promise<ListTacticalTradesResponse | null>;
  getTacticalSignals(
    params?: ListTacticalSignalsParams,
  ): Promise<ListTacticalSignalsResponse | null>;
  getTacticalBrief(): Promise<TacticalBrief | null>;
  getTacticalPerformance(windowDays: number): Promise<TacticalPerformance | null>;
}

/**
 * Backend accepts `day | intraday | hour | raw` on the snapshots endpoint; the
 * frontend UI uses `daily | hourly` which reads more naturally. This map keeps
 * the public API ergonomic without smearing translation logic across callers.
 */
const SNAPSHOT_GRANULARITY_WIRE: Record<SnapshotGranularity, string> = {
  daily: "day",
  hourly: "intraday",
};

/**
 * Create an `ApiClient` bound to the provided fetch + redirect configuration.
 * Call with no arguments in production; supply `{ fetchImpl }` in tests to
 * capture outgoing requests.
 */
export function createApiClient(config: ApiClientConfig = {}): ApiClient {
  const resolved = {
    fetchImpl: config.fetchImpl ?? ((input, init) => fetch(input, init)),
    loginUrl: config.loginUrl ?? DEFAULT_LOGIN_URL,
    csrfCookieName: config.csrfCookieName ?? DEFAULT_CSRF_COOKIE,
  } satisfies Required<Pick<ApiClientConfig, "fetchImpl" | "loginUrl" | "csrfCookieName">>;

  return {
    getMe() {
      return request<Owner>("/api/me", { method: "GET" }, resolved);
    },

    async logout() {
      await request<void>("/auth/logout", { method: "POST" }, resolved);
    },

    listProposals(params: ListProposalsParams = {}) {
      const qs = buildQueryString({
        status: params.status,
        from: params.from,
        to: params.to,
        limit: params.limit,
        cursor: params.cursor,
      });
      return request<ListProposalsResponse>(`/api/proposals${qs}`, { method: "GET" }, resolved);
    },

    getProposal(id: string) {
      return request<Proposal>(
        `/api/proposals/${encodeURIComponent(id)}`,
        { method: "GET" },
        resolved,
      );
    },

    decideProposal(id: string, choice: ProposalDecisionChoice) {
      return request<DecisionResponse>(
        `/api/proposals/${encodeURIComponent(id)}/decision`,
        { method: "POST", body: JSON.stringify({ choice }) },
        resolved,
      );
    },

    async listPortfolios() {
      const body = await request<{ portfolios: PortfolioSummary[] }>(
        "/api/portfolios",
        { method: "GET" },
        resolved,
      );
      return body.portfolios;
    },

    async getPortfolioSnapshots(id: number, params: GetPortfolioSnapshotsParams = {}) {
      const qs = buildQueryString({
        from: params.from,
        to: params.to,
        granularity:
          params.granularity !== undefined
            ? SNAPSHOT_GRANULARITY_WIRE[params.granularity]
            : undefined,
      });
      const body = await request<PortfolioSnapshotSeries>(
        `/api/portfolios/${id}/snapshots${qs}`,
        { method: "GET" },
        resolved,
      );
      return body.points;
    },

    getPortfolioCurrent(id: number) {
      return request<PortfolioCurrent>(
        `/api/portfolios/${id}/current`,
        { method: "GET" },
        resolved,
      );
    },

    // ─── Tactical AI ─────────────────────────────────────────────────────
    //
    // The backend answers `/api/tactical/*` with HTTP 503 when
    // TACTICAL_ENABLED=false. The frontend treats that as "feature
    // unavailable" rather than an error: each fetcher resolves to `null`
    // (or `void` for the action endpoints) so the dashboard can simply hide
    // the Tactical sections without rendering an error banner.

    async getTacticalStatus() {
      return tacticalRead<TacticalStatus>("/api/tactical/status", resolved);
    },

    async pauseTactical(reason: string) {
      await tacticalAction("/api/tactical/pause", JSON.stringify({ reason }), resolved);
    },

    async resumeTactical(confirm: boolean) {
      await tacticalAction("/api/tactical/resume", JSON.stringify({ confirm }), resolved);
    },

    async getTacticalPortfolio() {
      return tacticalRead<TacticalPortfolio>("/api/tactical/portfolio", resolved);
    },

    async getTacticalSnapshots(from: Iso8601, to: Iso8601) {
      const qs = buildQueryString({ from, to });
      const body = await tacticalRead<{ points: TacticalSnapshotPoint[] }>(
        `/api/tactical/portfolio/snapshots${qs}`,
        resolved,
      );
      return body ? body.points : null;
    },

    async getTacticalTrades(params: ListTacticalTradesParams = {}) {
      const qs = buildQueryString({
        from: params.from,
        to: params.to,
        limit: params.limit,
        offset: params.offset,
        cursor: params.cursor,
      });
      return tacticalRead<ListTacticalTradesResponse>(`/api/tactical/trades${qs}`, resolved);
    },

    async getTacticalSignals(params: ListTacticalSignalsParams = {}) {
      const qs = buildQueryString({ from: params.from, to: params.to });
      return tacticalRead<ListTacticalSignalsResponse>(`/api/tactical/signals${qs}`, resolved);
    },

    async getTacticalBrief() {
      return tacticalRead<TacticalBrief>("/api/tactical/brief/current", resolved);
    },

    async getTacticalPerformance(windowDays: number) {
      return tacticalRead<TacticalPerformance>(
        `/api/tactical/performance?window_days=${windowDays}`,
        resolved,
      );
    },
  };
}

/**
 * Read-only tactical fetcher: returns the typed body on 200 and `null` on
 * 503 (the `tactical_disabled` envelope code the backend emits when the
 * feature flag is off). Any other non-2xx surfaces as a regular ApiError so
 * real failures still bubble through to StateBoundary.
 */
async function tacticalRead<T>(
  path: string,
  config: Required<Pick<ApiClientConfig, "fetchImpl" | "loginUrl" | "csrfCookieName">>,
): Promise<T | null> {
  try {
    const raw = await request<unknown>(path, { method: "GET" }, config);
    return keysToCamel<T>(raw);
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) return null;
    throw err;
  }
}

/**
 * Mutating tactical fetcher (pause / resume). Resolves silently on 503 —
 * the UI renders nothing when the feature is off, so a "disable while
 * disabled" call from a stale dashboard tab is a no-op rather than an
 * error to surface.
 */
async function tacticalAction(
  path: string,
  body: string,
  config: Required<Pick<ApiClientConfig, "fetchImpl" | "loginUrl" | "csrfCookieName">>,
): Promise<void> {
  try {
    await request<void>(path, { method: "POST", body }, config);
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) return;
    throw err;
  }
}

// ─── Default, production-bound client ────────────────────────────────────────

/**
 * Process-wide `ApiClient` singleton bound to the global `fetch`. Import from
 * pages / components. Tests should prefer `createApiClient({ fetchImpl })` to
 * keep stubs isolated.
 */
export const apiClient: ApiClient = createApiClient();

// Re-export the default bound fetchers for ergonomic destructured imports.
export const getMe = (): Promise<Owner> => apiClient.getMe();
export const logout = (): Promise<void> => apiClient.logout();
export const listProposals = (params?: ListProposalsParams): Promise<ListProposalsResponse> =>
  apiClient.listProposals(params);
export const getProposal = (id: string): Promise<Proposal> => apiClient.getProposal(id);
export const decideProposal = (
  id: string,
  choice: ProposalDecisionChoice,
): Promise<DecisionResponse> => apiClient.decideProposal(id, choice);
export const listPortfolios = (): Promise<PortfolioSummary[]> => apiClient.listPortfolios();
export const getPortfolioSnapshots = (
  id: number,
  params?: GetPortfolioSnapshotsParams,
): Promise<PortfolioSnapshotPoint[]> => apiClient.getPortfolioSnapshots(id, params);
export const getPortfolioCurrent = (id: number): Promise<PortfolioCurrent> =>
  apiClient.getPortfolioCurrent(id);

export const getTacticalStatus = (): Promise<TacticalStatus | null> =>
  apiClient.getTacticalStatus();
export const pauseTactical = (reason: string): Promise<void> => apiClient.pauseTactical(reason);
export const resumeTactical = (confirm: boolean): Promise<void> =>
  apiClient.resumeTactical(confirm);
export const getTacticalPortfolio = (): Promise<TacticalPortfolio | null> =>
  apiClient.getTacticalPortfolio();
export const getTacticalSnapshots = (
  from: Iso8601,
  to: Iso8601,
): Promise<TacticalSnapshotPoint[] | null> => apiClient.getTacticalSnapshots(from, to);
export const getTacticalTrades = (
  params?: ListTacticalTradesParams,
): Promise<ListTacticalTradesResponse | null> => apiClient.getTacticalTrades(params);
export const getTacticalSignals = (
  params?: ListTacticalSignalsParams,
): Promise<ListTacticalSignalsResponse | null> => apiClient.getTacticalSignals(params);
export const getTacticalBrief = (): Promise<TacticalBrief | null> => apiClient.getTacticalBrief();
export const getTacticalPerformance = (windowDays: number): Promise<TacticalPerformance | null> =>
  apiClient.getTacticalPerformance(windowDays);
